import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { queryClient } from '@/clients';
import {
    fetchPrefetchedDetailQuery,
    getPrefetchedDetailQueryData,
    workspaceResourceDetailQueryOptions,
} from '@/queries/tanstack/prefetch';
import { pipelineService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { Pipeline } from '@/types';

import { PipelineActions, PipelineState } from '../types';

const patchPipelineCollections = (
    pipelines: Pipeline[],
    selectedPipeline: Pipeline | null,
    id: string,
    updates: Partial<Pipeline>,
) => ({
    pipelines: mergeEntityById(pipelines, id, updates),
    selectedPipeline: selectedPipeline?.id === id ? mergeEntity(selectedPipeline, updates) : selectedPipeline,
});

export const createPipelineActions: StateCreator<
    PipelineState & PipelineActions,
    [['zustand/immer', never]],
    [],
    PipelineActions
> = (set, get) => ({
    // Core
    fetchPipelines: async (params) => {
        set({ loading: true });

        try {
            const pipelines = await pipelineService.list(params);

            set({ pipelines, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load pipelines' });
        }
    },
    fetchPipelineById: async (id) => {
        const queryOptions = workspaceResourceDetailQueryOptions.pipelines(id);
        const cachedPipeline = getPrefetchedDetailQueryData(queryClient, queryOptions);

        set({
            loadingPipeline: !cachedPipeline,
            error: undefined,
            ...(cachedPipeline ? { selectedPipeline: cachedPipeline } : {}),
        });

        try {
            const pipeline = await fetchPrefetchedDetailQuery(queryClient, queryOptions);

            set({ selectedPipeline: pipeline, loadingPipeline: false });
        } catch (error) {
            captureException(error);

            set({ error: 'Failed to fetch pipeline detail', loadingPipeline: false });
        }
    },
    clearSelectedPipeline: () => {
        set({ selectedPipeline: null });
    },
    createPipeline: async (input) =>
        runSingleFlight(createSingleFlightKey('pipelines:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as Pipeline;

            set({ pipelines: appendEntity(get().pipelines, optimistic) });

            try {
                const pipeline = await pipelineService.create(input);

                set({ pipelines: replaceOptimisticEntityById(get().pipelines, tempId, pipeline) });

                return pipeline;
            } catch (error) {
                captureException(error);

                set({ pipelines: removeEntityById(get().pipelines, tempId), error: 'Failed to create pipeline' });
            }
        }),
    updatePipeline: async (id, updates) => {
        const prev = get().pipelines.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set({
            pipelines: mergeEntityById(get().pipelines, id, updates),
            selectedPipeline:
                get().selectedPipeline?.id === id
                    ? mergeEntity(get().selectedPipeline!, updates)
                    : get().selectedPipeline,
        });

        try {
            const pipeline = await pipelineService.update(id, updates);

            set({
                pipelines: mergeEntityById(get().pipelines, id, pipeline),
                selectedPipeline:
                    get().selectedPipeline?.id === id
                        ? mergeEntity(get().selectedPipeline!, pipeline)
                        : get().selectedPipeline,
            });
        } catch (error) {
            captureException(error);

            set({
                pipelines: replaceEntityById(get().pipelines, id, prev),
                selectedPipeline: get().selectedPipeline?.id === id ? prev : get().selectedPipeline,
                error: 'Failed to update pipeline',
            });
        }
    },
    deletePipeline: async (id) => {
        const prev = get().pipelines;

        set({
            pipelines: removeEntityById(get().pipelines, id),
            selectedPipeline: get().selectedPipeline?.id === id ? null : get().selectedPipeline,
        });

        try {
            await pipelineService.delete(id);
        } catch (error) {
            captureException(error);

            set({ pipelines: prev, error: 'Failed to delete pipeline' });
        }
    },
    applyPipelineDelta: (packet) => {
        if (packet.entity !== 'pipeline') {
            return;
        }

        const next = reduceEntityDelta<Pipeline>(get().pipelines, get().selectedPipeline, packet);

        if (!next) {
            return;
        }

        set({
            pipelines: next.items,
            selectedPipeline: next.selected,
        });
    },
    // - Aliases
    duplicatePipeline: async (id) => {
        const prev = get().pipelines;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await pipelineService.duplicate(id);

            set({ pipelines: upsertEntityById(get().pipelines, duplicated, { prepend: true }) });

            toast('Pipeline Duplicated', {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: { id: duplicated.id, type: 'pipeline' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ pipelines: prev, error: 'Failed to duplicate pipeline' });

            toast.error('Failed to duplicate pipeline');
        }
    },
    togglePipelineFavorite: async (id, isFavorite) => {
        const prev = get().pipelines.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set(patchPipelineCollections(get().pipelines, get().selectedPipeline, id, { isFavorite }));

        try {
            await pipelineService.setFavorite(id, isFavorite);

            toast(`${isFavorite ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(isFavorite ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'pipeline' },
            });
        } catch (error) {
            captureException(error);

            set({
                ...patchPipelineCollections(get().pipelines, get().selectedPipeline, id, {
                    isFavorite: prev.isFavorite,
                }),
                error: 'Failed to update favorite',
            });
        }
    },
    togglePipelineLock: async (id, isLocked) => {
        const prev = get().pipelines.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set(patchPipelineCollections(get().pipelines, get().selectedPipeline, id, { isLocked }));

        try {
            await pipelineService.setLocked(id, isLocked);

            toast(`Pipeline ${isLocked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(isLocked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'pipeline' },
            });
        } catch (error) {
            captureException(error);

            set({
                ...patchPipelineCollections(get().pipelines, get().selectedPipeline, id, {
                    isLocked: prev.isLocked,
                }),
                error: 'Failed to update favorite',
            });
        }
    },
    togglePipelineArchive: async (id, isArchived) => {
        const prev = get().pipelines.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set(patchPipelineCollections(get().pipelines, get().selectedPipeline, id, { isArchived }));

        try {
            await pipelineService.setArchived(id, isArchived);

            toast(`Pipeline ${isArchived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(isArchived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'pipeline' },
            });
        } catch (error) {
            captureException(error);

            set({
                ...patchPipelineCollections(get().pipelines, get().selectedPipeline, id, {
                    isArchived: prev.isArchived,
                }),
                error: 'Failed to update favorite',
            });
        }
    },
});
